import { create } from "zustand";
import { cacheService } from "@/services/cacheService";
import { showToast } from "@/lib/toast";
import {
  CacheStats,
  CacheType,
  ClientCacheType,
  ClientHealthResponse,
} from "@/types/cache";

interface CacheState {
  // State
  stats: CacheStats | null;
  clientHealth: ClientHealthResponse | null;
  isLoading: boolean;
  isClearing: boolean;
  isRevalidating: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // Actions
  fetchStats: () => Promise<void>;
  checkClientHealth: () => Promise<void>;
  clearCacheByType: (type: CacheType) => Promise<boolean>;
  clearAllCache: () => Promise<boolean>;
  revalidateClient: (type: ClientCacheType, slug?: string, episodeSlug?: string) => Promise<boolean>;
  revalidateAllClient: () => Promise<boolean>;
  clearAllAndRevalidate: () => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

export const useCacheStore = create<CacheState>((set, get) => ({
  // Initial state
  stats: null,
  clientHealth: null,
  isLoading: false,
  isClearing: false,
  isRevalidating: false,
  error: null,
  lastUpdated: null,

  // Actions
  fetchStats: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await cacheService.getCacheStats();

      set({
        stats: response.data.data,
        isLoading: false,
        lastUpdated: new Date(),
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi lấy thống kê cache";
      set({ error: errorMessage, isLoading: false });
      showToast.error("Lỗi", errorMessage);
    }
  },

  checkClientHealth: async () => {
    try {
      const response = await cacheService.checkClientHealth();

      set({ clientHealth: response.data });
    } catch (error) {
      set({
        clientHealth: {
          available: false,
          url: "",
          message: "Không thể kết nối đến client",
        },
      });
    }
  },

  clearCacheByType: async (type: CacheType) => {
    set({ isClearing: true, error: null });
    try {
      const result = await cacheService.clearCacheByType(type);
      showToast.success("Thành công", result.message);
      // Refresh stats after clearing
      await get().fetchStats();
      set({ isClearing: false });
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi xóa cache";
      set({ error: errorMessage, isClearing: false });
      showToast.error("Lỗi", errorMessage);
      return false;
    }
  },

  clearAllCache: async () => {
    set({ isClearing: true, error: null });
    try {
      const result = await cacheService.clearAllCache();
      showToast.success("Thành công", result.message);
      // Refresh stats after clearing
      await get().fetchStats();
      set({ isClearing: false });
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi xóa cache";
      set({ error: errorMessage, isClearing: false });
      showToast.error("Lỗi", errorMessage);
      return false;
    }
  },

  revalidateClient: async (type: ClientCacheType, slug?: string, episodeSlug?: string) => {
    set({ isRevalidating: true, error: null });
    try {
      const result = await cacheService.revalidateClient(type, slug, episodeSlug);
      showToast.success("Thành công", result.message);
      set({ isRevalidating: false });
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi revalidate client";
      set({ error: errorMessage, isRevalidating: false });
      showToast.error("Lỗi", errorMessage);
      return false;
    }
  },

  revalidateAllClient: async () => {
    set({ isRevalidating: true, error: null });
    try {
      const result = await cacheService.revalidateAllClient();
      showToast.success("Thành công", result.message);
      set({ isRevalidating: false });
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi revalidate client";
      set({ error: errorMessage, isRevalidating: false });
      showToast.error("Lỗi", errorMessage);
      return false;
    }
  },

  clearAllAndRevalidate: async () => {
    set({ isClearing: true, isRevalidating: true, error: null });
    try {
      const result = await cacheService.clearAllAndRevalidate();
      showToast.success("Thành công", result.message);
      // Refresh stats after clearing
      await get().fetchStats();
      set({ isClearing: false, isRevalidating: false });
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi xóa và revalidate";
      set({ error: errorMessage, isClearing: false, isRevalidating: false });
      showToast.error("Lỗi", errorMessage);
      return false;
    }
  },

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      stats: null,
      clientHealth: null,
      isLoading: false,
      isClearing: false,
      isRevalidating: false,
      error: null,
      lastUpdated: null,
    }),
}));
